import { RECOMMENDED_MAPS } from '../constants/mockData';

const hasMod = (mods, ...names) => {
  if (!mods) return false;
  if (Array.isArray(mods)) return names.some(n => mods.includes(n));
  return names.some(n => String(mods).includes(n));
};
const clamp = (v, lo = 0, hi = 99) => Math.round(Math.min(hi, Math.max(lo, v)));
const avg   = arr => arr.reduce((s, v) => s + v, 0) / (arr.length || 1);

function skillsFromBest(bestPlays) {
  if (!bestPlays?.length) return null;
  const top = bestPlays.slice(0, 10);

  const aim = Math.max(...top.map(p => {
    const base = (p.stars || 0) * 10;
    return base + (hasMod(p.mods,'HR') ? 8 : 0) + (hasMod(p.mods,'HD') ? 5 : 0)
      - Math.min(20, (p.miss_count ?? 0) * 2);
  }));

  const speed = Math.max(...top.map(p => {
    let bpm = p.bpm || 0;
    if (hasMod(p.mods,'DT','NC')) bpm *= 1.5;
    if (hasMod(p.mods,'HT'))      bpm *= 0.75;
    return bpm / 2.5 + (/speed|stream|dt|fast|burst/i.test(p.diff||'') ? 15 : 0);
  }));

  const acc = (avg(top.map(p => p.acc || 0)) - 88) * 6;

  const stamina = Math.max(...top.map(p => {
    const mins = (p.hit_length || 0) / 60;
    return Math.min(50, mins * 12) + ((p.acc || 0) > 95 && mins > 2 ? 20 : 0);
  }));

  const reading = Math.max(...top.map(p => {
    const ar = p.ar || 0;
    let base = ar < 9 ? 50 + (9-ar)*4 : ar > 10 ? 50 + (ar-10)*12 : 45;
    if (hasMod(p.mods,'FL')) base += 25;
    if (hasMod(p.mods,'HD')) base += 10;
    return base + ((p.acc||0) - 88) * 0.8;
  }));

  const tech = Math.max(...top.map(p => {
    const t = /tech|stream|alt|complex|collab|crossover|pattern/i.test(p.diff||'') ? 25 : 0;
    const s = /sv|scroll|slow|burai|yooh|pishi|sotarks/i.test(p.diff||'')          ? 20 : 0;
    return t + s + ((p.acc||0) - 88) * 1.2;
  }));

  return { aim, speed, acc, stamina, reading, tech };
}

function skillsFromRecent(recent) {
  const passed = recent?.filter(p => p.rank !== 'F') || [];
  if (!passed.length) return null;
  const last = passed.slice(0, 30);

  const aim = avg(last.map(p =>
    (p.stars||0)*10 + (hasMod(p.mods,'HR')?8:0) + (hasMod(p.mods,'HD')?5:0)
    - Math.min(20,(p.miss_count??0)*2)
  ));

  const speed = avg(last.map(p => {
    let bpm = p.bpm || 0;
    if (hasMod(p.mods,'DT','NC')) bpm *= 1.5;
    if (hasMod(p.mods,'HT'))      bpm *= 0.75;
    return bpm / 2.5 + (/speed|stream|dt|fast|burst/i.test(p.diff||'') ? 15 : 0);
  }));

  const acc = (avg(last.map(p => p.acc||0)) - 88) * 6;

  const longMaps    = last.filter(p => (p.hit_length||0) > 60);
  const avgLen      = longMaps.length ? avg(longMaps.map(p => (p.hit_length||0)/60)) : 1;
  const longHighAcc = longMaps.filter(p => (p.acc||0) > 95).length;
  const stamina     = Math.min(70, avgLen * 12) + Math.min(25, longHighAcc * 5);

  const reading = avg(last.map(p => {
    const ar = p.ar || 0;
    let base = ar < 9 ? 40+(9-ar)*3 : ar > 10 ? 40+(ar-10)*10 : 38;
    if (hasMod(p.mods,'FL')) base += 20;
    if (hasMod(p.mods,'HD')) base += 8;
    return base + ((p.acc||0)-88)*0.5;
  }));

  const tech = avg(last.map(p =>
    (/tech|stream|alt|complex|collab|crossover|pattern/i.test(p.diff||'') ? 20 : 0) +
    (/sv|scroll|slow|burai|yooh|pishi|sotarks/i.test(p.diff||'') ? 15 : 0) +
    ((p.acc||0)-88)*0.8
  ));

  return { aim, speed, acc, stamina, reading, tech };
}

function skillsFromStats(player) {
  const pp  = player?.pp || 0;
  const acc = player?.accuracy || 0;
  const topS = player?.topPlay?.stars || 0;
  const pc   = player?.play_count || player?.playCount || 0;
  const ppF  = Math.min(80, pp/80);
  const accF = Math.max(0, (acc-88)*6);
  return {
    aim:     30 + topS*8 + accF*0.3,
    speed:   30 + ppF*0.5,
    acc:     accF,
    stamina: 20 + Math.min(40, pc/400),
    reading: 30 + topS*8*0.8,
    tech:    25 + ppF*0.35,
  };
}

export function computeSkills(player) {
  if (!player) return defaultSkills();
  const best   = skillsFromBest(player.bestPlays || []);
  const recent = skillsFromRecent(player.recentActivity || []);
  const stats  = skillsFromStats(player);

  const mix = k => {
    if (best && recent) return best[k]*0.5 + recent[k]*0.5;
    if (best)           return best[k]*0.7 + stats[k]*0.3;
    if (recent)         return recent[k]*0.7 + stats[k]*0.3;
    return stats[k];
  };

  return [
    { skill:'Aim',      skillRu:'Прицел',      value: clamp(mix('aim'),     10) },
    { skill:'Speed',    skillRu:'Скорость',     value: clamp(mix('speed'),   10) },
    { skill:'Accuracy', skillRu:'Точность',     value: clamp(mix('acc'),     10) },
    { skill:'Stamina',  skillRu:'Выносливость', value: clamp(mix('stamina'), 10) },
    { skill:'Reading',  skillRu:'Чтение',       value: clamp(mix('reading'), 10) },
    { skill:'Tech',     skillRu:'Техника',      value: clamp(mix('tech'),    10) },
  ];
}

function defaultSkills() {
  return ['Aim','Speed','Accuracy','Stamina','Reading','Tech'].map((s,i) => ({
    skill: s,
    skillRu: ['Прицел','Скорость','Точность','Выносливость','Чтение','Техника'][i],
    value: 20,
  }));
}

export function computePPTrend(player) {
  if (!player) return [];
  if (player.ppHistory?.length > 0) {
    return player.ppHistory.map((h,i,arr) => ({
      date: h.date, pp: h.pp,
      delta: i > 0 ? h.pp - arr[i-1].pp : 0,
    }));
  }
  if (player.pp) {
    const pp = player.pp;
    return [
      { date:'Before', pp: Math.round(pp*0.88), delta:0 },
      { date:'Month',  pp: Math.round(pp*0.92), delta:0 },
      { date:'Now',    pp: Math.round(pp),      delta:0 },
    ];
  }
  return [];
}

export function recommendMaps(player, maps) {
  const topPP = player?.topPlay?.pp || player?.pp || 0;
  if (!topPP) return maps.map(m => ({ ...m, score:1, ppDiff:0, matchesWeak:false }));
  const skills    = computeSkills(player);
  const weakSkill = skills.slice().sort((a,b) => a.value-b.value)[0];
  return maps.map(m => {
    const ppDiff      = m.pp - topPP;
    const matchesWeak = weakSkill
      ? m.tags.some(t => t.toLowerCase().includes(weakSkill.skill.toLowerCase().slice(0,3)))
      : false;
    const score = (ppDiff>-100&&ppDiff<80?1:0) + (matchesWeak?0.5:0) + (1-Math.abs(ppDiff)/500);
    return { ...m, score, ppDiff, matchesWeak };
  }).sort((a,b) => b.score - a.score);
}